// Supervisor Academic Breaks — uses the same logic as the employee page,
// just rendered inside the SupervisorLayout. Backend endpoints are shared
// (/api/me/academic-breaks).
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import SupervisorLayout from '../../components/layout/SupervisorLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  GraduationCap,
  CalendarPlus,
  Calendar,
  Trash2,
  Edit2,
  X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays, differenceInMonths } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const todayStr = () => format(new Date(), 'yyyy-MM-dd');

const formatDuration = (start, end) => {
  try {
    const s = parseISO(start); const e = parseISO(end);
    const months = differenceInMonths(e, s);
    const days = differenceInDays(e, s) - months * 30;
    if (months > 0 && days > 0) return `${months}mo ${days}d`;
    if (months > 0) return `${months} month${months !== 1 ? 's' : ''}`;
    const t = differenceInDays(e, s) + 1;
    return `${t} day${t !== 1 ? 's' : ''}`;
  } catch { return ''; }
};

const statusFor = (start, end) => {
  try {
    const s = parseISO(start); const e = parseISO(end); const now = new Date();
    if (now >= s && now <= e) return { label: 'On Break Now', class: 'bg-amber-500/20 text-amber-400 border-amber-500/40' };
    if (now < s) return { label: 'Upcoming', class: 'bg-blue-500/20 text-blue-400 border-blue-500/40' };
    return { label: 'Past', class: 'bg-slate-500/20 text-slate-400 border-slate-500/40' };
  } catch { return { label: '', class: '' }; }
};

export default function SupervisorAcademicBreaksPage() {
  const [breaks, setBreaks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);

  const fetchBreaks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/me/academic-breaks`);
      setBreaks(res.data?.breaks || []);
    } catch { toast.error('Failed to load academic breaks'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBreaks(); }, [fetchBreaks]);

  const openAdd = () => { setForm({ start_date: '', end_date: '' }); setDialog({ open: true, editing: null }); };
  const openEdit = (b) => { setForm({ start_date: b.start_date, end_date: b.end_date }); setDialog({ open: true, editing: b }); };
  const close = () => { setDialog({ open: false, editing: null }); setForm({ start_date: '', end_date: '' }); };

  const handleSave = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Pick both dates'); return; }
    if (form.end_date < form.start_date) { toast.error('End date cannot be before start date'); return; }
    setSaving(true);
    try {
      if (dialog.editing) {
        await axios.put(`${API}/me/academic-breaks/${dialog.editing.id}`, form);
        toast.success('Break updated');
      } else {
        await axios.post(`${API}/me/academic-breaks`, form);
        toast.success('Break added');
      }
      close(); fetchBreaks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete break ${b.start_date} → ${b.end_date}?`)) return;
    try { await axios.delete(`${API}/me/academic-breaks/${b.id}`); toast.success('Break removed'); fetchBreaks(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <SupervisorLayout>
      <div className="p-4 sm:p-6 space-y-6 min-h-screen">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-primary" strokeWidth={1.5} />
              ACADEMIC BREAKS
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Add your university / college holiday periods.
            </p>
          </div>
          <Button onClick={openAdd} data-testid="add-break-btn" className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm uppercase tracking-wider">
            <CalendarPlus className="w-4 h-4 mr-2" /> Add Break
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground p-4">Loading…</p>
        ) : breaks.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-12 text-center space-y-2">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
              <p className="text-sm text-muted-foreground">No academic breaks yet.</p>
              <p className="text-xs text-muted-foreground/70">Tap "Add Break" to register your first holiday period.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {breaks.map((b, i) => {
              const status = statusFor(b.start_date, b.end_date);
              return (
                <Card key={b.id} className="bg-card border-border rounded-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-barlow tracking-wider text-sm uppercase">Break #{i + 1}</h3>
                      {status.label && <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${status.class}`}>{status.label}</span>}
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                        <span className="text-muted-foreground">From:</span>
                        <span className="font-mono">{format(parseISO(b.start_date), 'dd MMM yyyy')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-red-400 shrink-0" />
                        <span className="text-muted-foreground">To:</span>
                        <span className="font-mono">{format(parseISO(b.end_date), 'dd MMM yyyy')}</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground pl-6">Duration: {formatDuration(b.start_date, b.end_date)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <Button variant="outline" size="sm" onClick={() => openEdit(b)} className="border-border rounded-sm text-xs">
                        <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDelete(b)} className="border-destructive/30 text-destructive hover:bg-destructive/10 rounded-sm text-xs">
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              {dialog.editing ? 'Edit Academic Break' : 'Add Academic Break'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start Date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} className="bg-background border-border rounded-sm" min="2024-01-01" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">End Date</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))} className="bg-background border-border rounded-sm" min={form.start_date || todayStr()} />
            </div>
            {form.start_date && form.end_date && form.end_date >= form.start_date && (
              <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-sm">Duration: {formatDuration(form.start_date, form.end_date)}</p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={close} disabled={saving} className="border-border rounded-sm">
                <XIcon className="w-4 h-4 mr-1.5" /> Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || !form.start_date || !form.end_date} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm">
                {saving ? 'Saving…' : (dialog.editing ? 'Update' : 'Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SupervisorLayout>
  );
}
